import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

/** Coincidencia de una media query, segura para jsdom. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', cb);
      return () => mql.removeEventListener('change', cb);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  );
}

/** < 640 px (teléfono). */
export const usePhone = () => useMediaQuery('(max-width: 639px)');
/** < 768 px: hojas a pantalla completa. */
export const useCompact = () => useMediaQuery('(max-width: 767px)');
/** Puntero grueso (táctil). */
export const useCoarsePointer = () => useMediaQuery('(pointer: coarse)');

/** Valor con retardo (búsquedas con debounce de 200 ms). */
export function useDebouncedValue<T>(value: T, ms = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** true recién después de `ms` mientras `active` (evita parpadeos de carga < 300 ms). */
export function useDelayedFlag(active: boolean, ms = 300): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => setShown(true), ms);
    return () => {
      clearTimeout(t);
      setShown(false);
    };
  }, [active, ms]);
  return active && shown;
}

/** Estado controlado/no controlado al estilo Radix. */
export function useControllable<T>(
  value: T | undefined,
  defaultValue: T,
  onChange?: (v: T) => void,
): [T, (v: T) => void] {
  const [inner, setInner] = useState(defaultValue);
  const controlled = value !== undefined;
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  const set = useCallback(
    (v: T) => {
      if (!controlled) setInner(v);
      onChangeRef.current?.(v);
    },
    [controlled],
  );
  return [controlled ? (value as T) : inner, set];
}
